import { Link } from "react-router-dom";
import type { NewsItem, ResourceItem, TopicItem } from "@/mocks/content";
import { formatNumber, shortDate } from "@/lib/content";
import Badge from "@/components/base/Badge";

export function NewsListCard({ item }: { item: NewsItem }) {
  return (
    <Link
      to={`/detail/${item.id}`}
      className="group flex cursor-pointer flex-col gap-4 rounded-lg border border-background-200/90 bg-background-50 p-3 transition hover:border-primary-200 hover:bg-primary-50/40 sm:flex-row sm:p-4"
    >
      <div className="relative h-[168px] w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-[132px] sm:w-[200px]">
        <img
          src={item.cover}
          alt={`${item.title} 封面`}
          title={`${item.title} - 18禁资讯`}
          className="h-full w-full object-top object-cover transition duration-500 group-hover:scale-[1.03]"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Badge tone="primary">{item.category}</Badge>
          <span className="text-xs text-foreground-500">{shortDate(item.date)}</span>
        </div>
        <h3 className="text-[15px] font-semibold leading-snug text-foreground-950 transition group-hover:text-primary-700 md:text-base">
          {item.title}
        </h3>
        <p className="mt-2 line-clamp-2 text-[13px] leading-relaxed text-foreground-600">{item.summary}</p>
        <div className="mt-auto flex items-center gap-4 pt-3 text-[11px] text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-user-3-line text-xs" aria-hidden="true"></i>
            {item.author}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-xs text-accent-600" aria-hidden="true"></i>
            {formatNumber(item.hot)} 阅读
          </span>
        </div>
      </div>
    </Link>
  );
}

export function ResourceCard({ item }: { item: ResourceItem }) {
  return (
    <Link
      to={`/detail/${item.id}`}
      className="group flex cursor-pointer flex-col overflow-hidden rounded-lg border border-background-200/90 bg-background-50 transition hover:border-primary-200"
    >
      <div className="relative aspect-[3/4] w-full overflow-hidden bg-background-200">
        <img
          src={item.cover}
          alt={`${item.name} 封面`}
          title={`${item.name} - ${item.typeLabel}资源 - 18禁`}
          className="h-full w-full object-top object-cover transition duration-500 group-hover:scale-[1.04]"
        />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-primary-950/80 to-transparent"></div>
        <span className="absolute left-2.5 top-2.5 rounded-sm bg-background-50/92 px-2 py-0.5 text-[10px] font-medium tracking-wide text-primary-700">
          {item.typeLabel}
        </span>
        <span className="absolute bottom-2.5 left-2.5 flex items-center gap-1 text-[11px] font-medium text-background-50">
          <i className="ri-star-fill text-accent-400" aria-hidden="true"></i>
          {item.rating.toFixed(1)}
        </span>
        <span className="absolute bottom-2.5 right-2.5 flex items-center gap-1 text-[11px] text-background-100">
          <i className="ri-bookmark-line" aria-hidden="true"></i>
          {formatNumber(item.favorites)}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-3.5">
        <h3 className="truncate text-sm font-semibold text-foreground-950 transition group-hover:text-primary-700">{item.name}</h3>
        <p className="mt-1.5 line-clamp-2 text-[12px] leading-relaxed text-foreground-600">{item.summary}</p>
        <div className="mt-auto flex flex-wrap gap-1.5 pt-3">
          {item.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="rounded-sm bg-secondary-100 px-1.5 py-0.5 text-[10px] text-secondary-800">
              {tag}
            </span>
          ))}
          <span className="rounded-sm bg-background-100 px-1.5 py-0.5 text-[10px] text-foreground-500">{item.region}</span>
        </div>
      </div>
    </Link>
  );
}

export function TopicRow({ item, index }: { item: TopicItem; index?: number }) {
  return (
    <Link
      to={`/detail/${item.id}`}
      className="group flex cursor-pointer gap-4 rounded-lg border border-background-200/90 bg-background-50 px-4 py-4 transition hover:border-primary-200 hover:bg-primary-50/30"
    >
      {typeof index === "number" ? (
        <span
          className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-xs font-semibold ${
            index < 3 ? "bg-accent-100 text-accent-800" : "bg-background-100 text-foreground-500"
          }`}
        >
          {index + 1}
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-semibold leading-snug text-foreground-950 transition group-hover:text-primary-700 md:text-[15px]">
          {item.title}
        </h3>
        <p className="mt-1.5 line-clamp-2 text-[12px] leading-relaxed text-foreground-600">{item.summary}</p>
        <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-foreground-500">
          <span>{item.author}</span>
          <span className="text-background-300">|</span>
          <span className="flex items-center gap-1">
            <i className="ri-chat-3-line text-xs" aria-hidden="true"></i>
            {item.replies} 回复
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line text-xs" aria-hidden="true"></i>
            {formatNumber(item.views)}
          </span>
          {item.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="rounded-sm bg-secondary-100 px-1.5 py-0.5 text-[10px] text-secondary-800">
              #{tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}